// pages/PublicLeadFormPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle, Link2 } from 'lucide-react';
import { leadFormsApi, PublicLeadFormData } from '../services/api';
import { PublicLeadFormContent } from '../components/PublicLeadFormContent';

export const PublicLeadFormPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<PublicLeadFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState('');

  useEffect(() => {
    if (slug) loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadForm = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await leadFormsApi.getPublic(slug!);
      setData(res);
    } catch (err: any) {
      console.error('Erro ao carregar formulário:', err);
      setError(err.message || 'Formulário não encontrado ou desativado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload: Record<string, any>) => {
    if (!slug) return;
    setSubmitting(true);
    try {
      const res = await leadFormsApi.submitPublic(slug, payload);
      setSubmitted(true);
      setSubmittedMessage(res.message || '');
    } catch (err: any) {
      setError(err.message || 'Não foi possível registar a submissão. Tente novamente.');
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Formulário indisponível</h2>
          <p className="text-sm text-slate-500">{error}</p>
          <a
            href="https://meupontodevenda.com"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
          >
            <Link2 size={16} /> Conheça o MPV
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <PublicLeadFormContent
      data={data}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitted={submitted}
      submittedMessage={submittedMessage}
    />
  );
};
